import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import { Box, Button, Hidden, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { VideoPreviewWithControls } from 'components';
import { ConnectionTestDialog } from 'components/ConnectionTest/ConnectionTestDialog';
import { PatientWaitingRoom, PatientWaitingRoomSkeleton, ChatButton } from './components';
import { useOpenChatOnNewMessage, useSignalrConnection } from 'hooks';
import { ISignalrConnectionContext } from 'context';
import { useAppDispatch, useAppSelector } from 'store';
import { clearPatientQueueStore } from 'store/patientQueue';
import { selectPatientIsDismissed } from 'store/patient';
import { getWaitingRoomProfile, selectWaitingRoomLoading, selectWaitingRoomProfile } from 'store/waitingRoom';

import styles from './LinkPage.module.scss';

export function LinkPage() {
  // will be moved to Chat component
  useOpenChatOnNewMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation('link');
  const dispatch = useAppDispatch();
  const { link } = useParams();
  const waitingRoomProfile = useAppSelector(selectWaitingRoomProfile);
  const waitingRoomLoading = useAppSelector(selectWaitingRoomLoading);
  const isDismissed = useAppSelector(selectPatientIsDismissed);
  const { disconnect }: ISignalrConnectionContext = useSignalrConnection();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [connectionTestOpen, setConnectionTestOpen] = useState(false);

  const leaveWaitingQueue = useCallback(() => {
    dispatch(clearPatientQueueStore());
    disconnect();
  }, [dispatch]);

  useEffect(() => {
    if (link) {
      dispatch(getWaitingRoomProfile(link));
    }
  }, [link]);

  useEffect(() => {
    return () => {
      leaveWaitingQueue();
    };
  }, []);

  useEffect(() => {
    window.addEventListener('beforeunload', leaveWaitingQueue);
    return () => {
      window.removeEventListener('beforeunload', leaveWaitingQueue);
    };
  }, [leaveWaitingQueue]);

  useEffect(() => {
    const syncForcedLightTheme = () => {
      const shouldForceLightTheme = localStorage.getItem('app-theme') === null;
      if (shouldForceLightTheme) {
        document.body.classList.add('patient-waiting-room-force-light');
      } else {
        document.body.classList.remove('patient-waiting-room-force-light');
      }
    };

    const handleThemeChange = () => {
      syncForcedLightTheme();
    };

    syncForcedLightTheme();
    window.addEventListener('app-theme-change', handleThemeChange as EventListener);
    window.addEventListener('storage', handleThemeChange);

    return () => {
      window.removeEventListener('app-theme-change', handleThemeChange as EventListener);
      window.removeEventListener('storage', handleThemeChange);
      document.body.classList.remove('patient-waiting-room-force-light');
    };
  }, []);

  useEffect(() => {
    if (!waitingRoomLoading && !waitingRoomProfile) {
      navigate('/notFound');
    }
  }, [waitingRoomLoading, waitingRoomProfile]);

  useEffect(() => {
    const supported = new Set(['en', 'es', 'br', 'fr', 'zh', 'ru', 'ar']);
    const params = new URLSearchParams(location.search);

    let selectedLanguage = (params.get('lang') || '').toLowerCase();
    if (!selectedLanguage) {
      for (const [key, value] of params.entries()) {
        if (!value && supported.has(key.toLowerCase())) {
          selectedLanguage = key.toLowerCase();
          break;
        }
      }
    }

    if (selectedLanguage && supported.has(selectedLanguage) && i18n.resolvedLanguage !== selectedLanguage) {
      i18n.changeLanguage(selectedLanguage).catch(() => undefined);
    }

    if (selectedLanguage && supported.has(selectedLanguage)) {
      const nextUrl = `${location.pathname}${location.hash || ''}`;
      window.history.replaceState({}, '', nextUrl);
    }
  }, [i18n, location.search]);

  return (
    <div className={styles.container}>
      {!waitingRoomProfile ? <PatientWaitingRoomSkeleton /> : <PatientWaitingRoom profile={waitingRoomProfile} />}

      {waitingRoomProfile && !isDismissed && (
        <>
          <Hidden mdDown>
            <Box className={styles.sidebar} maxWidth={240} height={'100%'}>
              {!isDismissed && <VideoPreviewWithControls />}
              {!isDismissed && (
                <Stack
                  direction='column'
                  alignItems='center'
                  justifyContent='center'
                  sx={{ gap: '6px', mt: 1, px: 1, width: '100%' }}
                >
                  <Typography variant='body2' sx={{ fontSize: '1.2rem' }}>
                    {t('patientWaitingRoom.havingIssues')}
                  </Typography>
                  <Button
                    variant='outlined'
                    size='small'
                    sx={{ textTransform: 'none' }}
                    startIcon={<NetworkCheckIcon sx={{ width: 20, height: 20 }} />}
                    onClick={() => setConnectionTestOpen(true)}
                  >
                    {t('patientWaitingRoom.testYourConnection')}
                  </Button>
                </Stack>
              )}
              {waitingRoomProfile?.providerId && <ChatButton provider={waitingRoomProfile!} />}
            </Box>
          </Hidden>

          <Hidden mdUp>
            {waitingRoomProfile?.providerId && (
              <ChatButton className={styles.chatButton} provider={waitingRoomProfile!} hideToggleButton={isMobile} />
            )}
          </Hidden>
        </>
      )}
      <ConnectionTestDialog open={connectionTestOpen} onClose={() => setConnectionTestOpen(false)} />
    </div>
  );
}
